// components/PropertyFilters.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  Slider,
  Button,
  Chip,
  // Grid,
  // Grid2,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import AddIcon from "@mui/icons-material/Add";
// import RemoveIcon from "@mui/icons-material/Remove";

import { CurrencyRupee } from "@mui/icons-material";
import { useGetAmenitiesQuery } from "../features/amenities/api/amenitiesApiSlice";
import { AmenityIcon } from "../utils/amenityIcons";
import { BathroomOutlinedIcon } from "../utils/bathroomTypeIcons";
import { SharingOutlinedIcon } from "../utils/sharingTypeIcons";
import { TenantOutlinedIcon } from "../utils/tenantTypeIcons";
import { GenderIcon } from "../utils/genderIcons";

// --- CONSTANTS (Stable Data) ---
const GENDER_OPTIONS = [
  { value: "male", label: "Men" },
  { value: "female", label: "Women" },
  // { value: "coed", label: "Unisex" },
];

const TENANT_TYPES = [
  { value: "students", label: "Students" },
  {
    value: "professionals",
    label: "Professionals",
  },
  { value: "both", label: "Both" },
];

const SHARING_TYPES = [
  { value: "1", label: "Private Room" },
  { value: "2", label: "Double Sharing" },
  {
    value: "3",
    label: "Triple Sharing",
  },
  { value: "4", label: "4+ Sharing" },
];

const BATHROOM_TYPES = ["Attached", "Common"];

// --- SUB-COMPONENTS ---

const FilterSection = ({ title, children }) => (
  <Box>
    <Typography
      variant="subtitle2"
      fontWeight={600}
      gutterBottom
      sx={{ mb: 1.5 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

{
  /* <Typography variant="subtitle1" gutterBottom>
  Amenities
</Typography>; */
}

// // Number Counter Component
// export const NumberCounter = ({ label, value, onIncrement, onDecrement }) => (
//   <Box sx={{ textAlign: "center", p: 1 }}>
//     <Typography variant="body2" color="text.secondary" gutterBottom>
//       {label}
//     </Typography>
//     <Box
//       sx={{
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         gap: 1,
//       }}
//     >
//       <IconButton
//         size="small"
//         onClick={onDecrement}
//         disabled={value === 0}
//         sx={{
//           border: 1,
//           borderColor: "grey.300",
//           borderRadius: 1,
//         }}
//       >
//         <RemoveIcon fontSize="small" />
//       </IconButton>
//       <Typography variant="h6" sx={{ minWidth: 30, fontWeight: "bold" }}>
//         {value}
//       </Typography>
//       <IconButton
//         size="small"
//         onClick={onIncrement}
//         sx={{
//           border: 1,
//           borderColor: "grey.300",
//           borderRadius: 1,
//         }}
//       >
//         <AddIcon fontSize="small" />
//       </IconButton>
//     </Box>
//   </Box>
// );

function ValueLabelComponent(props) {
  const { children, value } = props;

  return (
    <Tooltip
      enterTouchDelay={0}
      placement="top"
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CurrencyRupee sx={{ fontSize: 16 }} />
          {/* {value.toLocaleString()} */}
          <Typography variant="body2" component="span">
            {value.toLocaleString()}
          </Typography>
        </Box>
      }
      arrow
    >
      {children}
    </Tooltip>
  );
}

export const PriceRangeFilter = ({ minPrice, maxPrice, onPriceChange }) => {
  const [sliderValue, setSliderValue] = React.useState([minPrice, maxPrice]);

  React.useEffect(() => {
    setSliderValue([minPrice, maxPrice]);
  }, [minPrice, maxPrice]);

  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
  };

  const handleSliderChangeCommitted = (event, newValue) => {
    onPriceChange(event, newValue);
  };

  const handleInputChange = (type) => (event) => {
    const value = parseInt(event.target.value) || 0;
    if (type === "min") {
      onPriceChange(event, [value, maxPrice]);
    } else {
      onPriceChange(event, [minPrice, value]);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        display="flex"
        alignItems="center" // Keeps the label and icon row centered
        sx={{ mb: 1 }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={600}
          display="flex"
          alignItems="center"
        >
          {/* Price Range */}
          Monthly Rent
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              ml: 0.5, // Space between text and symbol
              color: "text.secondary",
              // The "Secret Sauce": nudge it UP if it looks too low
              transform: "translateY(-1px)",
            }}
          >
            (<CurrencyRupee sx={{ fontSize: 16 }} />)
          </Box>
        </Typography>
      </Box>

      <Slider
        value={sliderValue}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderChangeCommitted}
        valueLabelDisplay="auto"
        // valueLabelFormat={(value) => `AED ${value.toLocaleString()}`}
        min={0}
        max={20000}
        step={500}
        color="primary"
        sx={{ mb: 3 }}
        slots={{ valueLabel: ValueLabelComponent }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          fullWidth
          label="Min Price"
          size="small"
          type="number"
          value={minPrice}
          onChange={handleInputChange("min")}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                      fontSize: 17,
                    }}
                  >
                    {/* ₹ */}
                    <CurrencyRupee fontSize="inherit" />
                  </Typography>
                </InputAdornment>
              ),
            },
            htmlInput: {
              min: 0,
              step: 500,
            },
          }}
          // InputProps={{
          //   startAdornment: (
          //     <InputAdornment position="start">AED</InputAdornment>
          //   ),
          // }}
        />
        <TextField
          fullWidth
          label="Max Price"
          size="small"
          type="number"
          value={maxPrice}
          onChange={handleInputChange("max")}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                      fontSize: 17,
                    }}
                  >
                    {/* ₹ */}
                    <CurrencyRupee fontSize="inherit" />
                  </Typography>
                </InputAdornment>
              ),
            },
            htmlInput: {
              min: 0,
              step: 500,
            },
          }}
          // InputProps={{
          //   startAdornment: (
          //     <InputAdornment position="start">AED</InputAdornment>
          //   ),
          // }}
        />
      </Box>
    </Box>
  );
};

// // Price Range Filter Component
// export const PriceRangeFilter = ({ minPrice, maxPrice, onPriceChange }) => {
//   const handleSliderChange = (event, newValue) => {
//     onPriceChange(newValue[0], newValue[1]);
//   };

//   const handleInputChange = (type) => (event) => {
//     const value = parseInt(event.target.value) || 0;
//     if (type === "min") {
//       onPriceChange(value, maxPrice);
//     } else {
//       onPriceChange(minPrice, value);
//     }
//   };

//   return (
//     <Box sx={{ width: "100%" }}>
//       <Typography variant="subtitle1" gutterBottom>
//         Price Range (AED)
//       </Typography>

//       <Slider
//         value={[minPrice, maxPrice]}
//         onChange={handleSliderChange}
//         valueLabelDisplay="auto"
//         valueLabelFormat={(value) => `AED ${value.toLocaleString()}`}
//         min={0}
//         max={20000}
//         step={500}
//         color="primary"
//         sx={{ mb: 3 }}
//       />

//       <Box sx={{ display: "flex", gap: 2 }}>
//         <TextField
//           fullWidth
//           label="Min Price"
//           type="number"
//           value={minPrice}
//           onChange={handleInputChange("min")}
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">AED</InputAdornment>
//             ),
//           }}
//         />
//         <TextField
//           fullWidth
//           label="Max Price"
//           type="number"
//           value={maxPrice}
//           onChange={handleInputChange("max")}
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">AED</InputAdornment>
//             ),
//           }}
//         />
//       </Box>
//     </Box>
//   );
// };

// Main Filters Component
export const PropertyFilters = ({
  filters,
  // propertyTypes,
  // amenitiesList,
  onPriceChange,
  onFilterChange,
  // onNumberChange,
  onAmenityToggle,
  // onPropertyTypeChange,
  onClearAll,
  onApplyFilters,
  onClose,
  loading = false,
}) => {
  const { data: amenitiesData, isLoading: loadingAmenities } =
    useGetAmenitiesQuery();

  // 2. Memoized Filtering: Only show 'property' or 'both' categories in this form
  const buildingAmenities = useMemo(() => {
    if (!amenitiesData) return [];
    // Handle RTK Query entity pattern if you use it, else assume array
    const list = amenitiesData.ids
      ? amenitiesData.ids.map((id) => amenitiesData.entities[id])
      : amenitiesData;
    return list.filter(
      (a) => a.category === "property" || a.category === "both"
    );
  }, [amenitiesData]);

  // console.log(171, buildingAmenities);
  // Helper to toggle array items (like amenities)
  // const handleArrayToggle = (field, value) => {
  //   const current = filters[field] || [];
  //   const updated = current.includes(value)
  //     ? current.filter((item) => item !== value)
  //     : [...current, value];
  //   onFilterChange(field, updated);
  // };

  // Helper for single select
  const handleSingleChange = (field, value) => {
    // Toggle off if clicking the same selected item
    // const newValue = filters[field] === value ? "" : value;
    const newValue = filters[field] === value ? null : value;
    onFilterChange(field, newValue);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        {/* <Typography variant="h5">Filters</Typography> */}
        <Typography variant="h6" fontWeight={700}>
          Filters
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={4}>
        {/* Price Range */}
        <PriceRangeFilter
          minPrice={filters.minPrice}
          maxPrice={filters.maxPrice}
          onPriceChange={onPriceChange}
        />

        <Divider />

        {/* Scrollable Content */}
        {/* <Stack spacing={4} sx={{ flex: 1, overflowY: "auto", px: 1, pb: 0 }}> */}
        <Stack spacing={4}>
          {/* <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
            Ideally For
          </Typography> */}
          {/* 1. Gender (Crucial for PGs) */}
          <FilterSection title="Gender">
            <Stack direction="row" spacing={1}>
              {GENDER_OPTIONS.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  icon={<GenderIcon iconKey={opt.value} />}
                  clickable
                  onClick={() => handleSingleChange("gender", opt.value)}
                  variant={filters.gender === opt.value ? "filled" : "outlined"}
                  color={filters.gender === opt.value ? "primary" : "default"}
                  sx={{
                    "& .MuiChip-icon": {
                      fontSize: "1.1rem", // Default is usually 1.25rem or 20px
                      ml: "8px", // Adjust margin if it looks cramped
                    },
                  }}
                />
              ))}
            </Stack>
          </FilterSection>

          {/* 5. Tenant Type */}
          <FilterSection title="Ideally For">
            {/* <FilterSection title="Tenant"> */}
            <Stack direction="row" spacing={1}>
              {TENANT_TYPES.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  icon={<TenantOutlinedIcon iconKey={type.value} />}
                  clickable
                  onClick={() => handleSingleChange("tenantType", type.value)}
                  variant={
                    filters.tenantType === type.value ? "filled" : "outlined"
                  }
                  color={
                    filters.tenantType === type.value ? "primary" : "default"
                  }
                  sx={{
                    "& .MuiChip-icon": {
                      fontSize: "1.1rem", // Default is usually 1.25rem or 20px
                      ml: "8px", // Adjust margin if it looks cramped
                    },
                  }}
                />
              ))}
            </Stack>
          </FilterSection>
        </Stack>

        <Divider />

        <Stack spacing={3}>
          {/* <Box> */}
          {/* <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
            Room Configuration
          </Typography> */}
          {/* 2. Sharing Type (Replaces Bedrooms) */}
          <FilterSection title="Room Sharing">
            <Box display="flex" flexWrap="wrap" gap={1}>
              {SHARING_TYPES.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  icon={<SharingOutlinedIcon iconKey={type.value} />}
                  clickable
                  onClick={() => handleSingleChange("sharingType", type.value)} // You need to handle 'sharingType' in Redux
                  variant={
                    filters.sharingType === type.value ? "filled" : "outlined"
                  }
                  color={
                    filters.sharingType === type.value ? "primary" : "default"
                  }
                  sx={{
                    mb: 1, // Target the internal icon class
                    "& .MuiChip-icon": {
                      fontSize: "1.1rem", // Default is usually 1.25rem or 20px
                      ml: "8px", // Adjust margin if it looks cramped
                    },
                  }}
                />
              ))}
            </Box>
          </FilterSection>

          {/* 3. Bathroom Type */}
          <FilterSection title="Bathroom">
            <Stack direction="row" spacing={1}>
              {BATHROOM_TYPES.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  icon={<BathroomOutlinedIcon iconKey={type.toLowerCase()} />}
                  clickable
                  onClick={() =>
                    handleSingleChange("bathroomType", type.toLowerCase())
                  }
                  variant={
                    filters.bathroomType === type.toLowerCase()
                      ? "filled"
                      : "outlined"
                  }
                  color={
                    filters.bathroomType === type.toLowerCase()
                      ? "primary"
                      : "default"
                  }
                  sx={{
                    // mb: 1, // Target the internal icon class
                    "& .MuiChip-icon": {
                      fontSize: "1.1rem", // Default is usually 1.25rem or 20px
                      ml: "8px", // Adjust margin if it looks cramped
                    },
                  }}
                />
              ))}
            </Stack>
          </FilterSection>
        </Stack>
        {/* </Box> */}

        <Divider />

        <Stack spacing={4}>
          {/* <Box> */}
          {/* <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
            Room Configuration
          </Typography> */}
          {/* 2. Sharing Type (Replaces Bedrooms) */}
          <FilterSection title="Amenities">
            {/* <Box display="flex" flexWrap="wrap" gap={1}> */}

            <Stack direction="row" flexWrap="wrap" gap={1}>
              {/* {amenitiesList.map((amenity) => ( */}
              {buildingAmenities.map((amenity) => (
                // <Chip
                //   key={amenity}
                //   label={amenity}
                //   clickable
                //   variant={
                //     filters.amenities.includes(amenity) ? "filled" : "outlined"
                //   }
                //   color={
                //     filters.amenities.includes(amenity) ? "primary" : "default"
                //   }
                //   onClick={() => onAmenityToggle(amenity)}
                //   // sx={{ mb: 1 }}
                // />
                <Chip
                  key={amenity._id}
                  label={amenity.name}
                  icon={
                    <AmenityIcon iconKey={amenity.key} sx={{ fontSize: 14 }} />
                  }
                  clickable
                  variant={
                    filters.amenities.includes(amenity._id)
                      ? "filled"
                      : "outlined"
                  }
                  color={
                    filters.amenities.includes(amenity._id)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => onAmenityToggle(amenity._id)}
                  // sx={{ mb: 1 }}
                  sx={{
                    // mb: 1, // Target the internal icon class
                    "& .MuiChip-icon": {
                      fontSize: "1.1rem", // Default is usually 1.25rem or 20px
                      ml: "8px", // Adjust margin if it looks cramped
                    },
                    // // If you want the label text smaller too for balance:
                    // "& .MuiChip-label": {
                    //   fontSize: "0.8125rem",
                    // },
                  }}
                />
              ))}
            </Stack>
            {/* </Box> */}
          </FilterSection>
        </Stack>

        {/* Bedrooms & Bathrooms */}
        {/* <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
            Rooms
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 item xs={6}>
              <NumberCounter
                label="Bedrooms"
                value={filters.bedrooms}
                onIncrement={() => onNumberChange("bedrooms", 1)}
                onDecrement={() => onNumberChange("bedrooms", -1)}
              />
            </Grid2>
            <Grid2 item xs={6}>
              <NumberCounter
                label="Bathrooms"
                value={filters.bathrooms}
                onIncrement={() => onNumberChange("bathrooms", 1)}
                onDecrement={() => onNumberChange("bathrooms", -1)}
              />
            </Grid2>
          </Grid2>
        </Box>

        <Divider /> */}

        {/* Property Type */}
        {/* <Box>
          <Typography variant="subtitle1" gutterBottom>
            Property Type
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {propertyTypes.map((type) => (
              <Chip
                key={type}
                label={type}
                clickable
                variant={filters.propertyType === type ? "filled" : "outlined"}
                color={filters.propertyType === type ? "primary" : "default"}
                onClick={() => onPropertyTypeChange(type)}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Box>

        <Divider /> */}

        {/* Amenities */}
        {/* <Box>
          <Typography variant="subtitle1" gutterBottom>
            Amenities
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {amenitiesList.map((amenity) => (
              <Chip
                key={amenity}
                label={amenity}
                clickable
                variant={
                  filters.amenities.includes(amenity) ? "filled" : "outlined"
                }
                color={
                  filters.amenities.includes(amenity) ? "primary" : "default"
                }
                onClick={() => onAmenityToggle(amenity)}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Box> */}

        {/* Action Buttons */}
        <Box
          //sx={{ display: "flex", gap: 2, pt: 2 }}
          sx={{
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 2,
          }}
        >
          <Button variant="outlined" fullWidth onClick={onClearAll}>
            Clear All
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onApplyFilters}
            size="large"
            disabled={loading}
          >
            Apply Filters
          </Button>
        </Box>
      </Stack>
    </>
  );
};
